import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { AtSign, MessageCircle, Plane, Clock, Ticket, Megaphone, ClipboardList, FileWarning, Bell, BellOff, PartyPopper, Bug, ShoppingCart, ChevronDown, Handshake } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { pushSupported, isPushActive, enablePush } from "@/lib/push";
import { useChatDock, type DockConv } from "./ChatDock";

// A chat notification links to /chat?dm=<id> or /chat?channel=<id>; turn that into
// a dock conversation so the click opens the floating chat instead of navigating.
function chatConvFromLink(link?: string | null): DockConv | null {
  if (!link || !link.startsWith("/chat")) return null;
  const p = new URLSearchParams(link.split("?")[1] || "");
  const dm = p.get("dm"), channel = p.get("channel");
  if (dm) return { kind: "dm", id: dm, name: "" };
  if (channel) return { kind: "channel", id: channel, name: "" };
  return null;
}

const SOUND_PREF_KEY = "scenta-hq-sound-on";
function readSoundPref(): boolean {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(SOUND_PREF_KEY);
  return v === null ? true : v === "1";
}

// Single shared AudioContext lazily created on first interaction.
let audioCtx: AudioContext | null = null;
function getAudio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (audioCtx) return audioCtx;
  const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
  if (!Ctx) return null;
  try {
    audioCtx = new Ctx();
    return audioCtx;
  } catch {
    return null;
  }
}

// A single note in a motif.
interface Note { f: number; t: number; d: number; type?: OscillatorType }
// One recognizable motif per sound family. Distinct melody + timbre so you can
// tell what kind of notification it is by ear.
const MOTIFS: Record<string, Note[]> = {
  // bright ascending triad — someone @mentioned you
  mention: [{ f: 880, t: 0, d: 0.12, type: "triangle" }, { f: 1175, t: 0.1, d: 0.12, type: "triangle" }, { f: 1568, t: 0.2, d: 0.2, type: "triangle" }],
  // friendly two-tone "text" chime — DM / chat
  dm:      [{ f: 660, t: 0, d: 0.12 }, { f: 988, t: 0.11, d: 0.18 }],
  // descending double — support ticket
  ticket:  [{ f: 784, t: 0, d: 0.12, type: "triangle" }, { f: 523, t: 0.12, d: 0.2, type: "triangle" }],
  // calm major arpeggio — time off
  pto:     [{ f: 523, t: 0, d: 0.11 }, { f: 659, t: 0.1, d: 0.11 }, { f: 784, t: 0.2, d: 0.2 }],
  // two flat beeps — time clock
  clock:   [{ f: 720, t: 0, d: 0.12, type: "square" }, { f: 720, t: 0.16, d: 0.12, type: "square" }],
  // rising fanfare — announcement
  announce:[{ f: 587, t: 0, d: 0.14, type: "triangle" }, { f: 880, t: 0.14, d: 0.14, type: "triangle" }, { f: 1175, t: 0.28, d: 0.24, type: "triangle" }],
  // happy 4-note run — kudos
  kudos:   [{ f: 523, t: 0, d: 0.1 }, { f: 659, t: 0.1, d: 0.1 }, { f: 784, t: 0.2, d: 0.1 }, { f: 1047, t: 0.3, d: 0.22 }],
  // harsh repeating alarm — discipline / expiring / urgent
  urgent:  [{ f: 988, t: 0, d: 0.13, type: "square" }, { f: 740, t: 0.14, d: 0.13, type: "square" }, { f: 988, t: 0.28, d: 0.13, type: "square" }, { f: 740, t: 0.42, d: 0.2, type: "square" }],
  // two-tone — order update
  order:   [{ f: 880, t: 0, d: 0.12, type: "triangle" }, { f: 587, t: 0.12, d: 0.18, type: "triangle" }],
  // default chime
  default: [{ f: 660, t: 0, d: 0.12, type: "triangle" }, { f: 988, t: 0.11, d: 0.2, type: "triangle" }],
};

function soundKeyFor(category?: string | null): keyof typeof MOTIFS {
  const c = category ?? "";
  if (c === "chat_mention" || c === "crm_assigned") return "mention"; // something was handed to you: the bright triad
  if (c === "dm" || c.startsWith("chat_")) return "dm";
  if (c.startsWith("ticket_")) return "ticket";
  if (c.startsWith("pto_") || c === "callout_sick") return "pto";
  if (c === "clock_warn" || c === "clock_auto_close" || c.startsWith("overage_")) return "clock";
  if (c === "announcement" || c === "feature") return "announce";
  if (c === "kudos") return "kudos";
  if (c === "discipline" || c.startsWith("expire_") || c.includes("expire")) return "urgent";
  if (c.startsWith("order_")) return "order";
  return "default";
}

// A sound the browser refused (no click on the page yet, or the tab was asleep) is kept
// and played the moment the person next touches the page or comes back to the tab.
let pendingSound: { category: string | null } | null = null;

async function playNotifSound(category?: string | null) {
  const ctx = getAudio();
  if (!ctx) return;
  // CRITICAL: must AWAIT resume(). Previously fire-and-forget — the oscillators
  // got scheduled while the context was still suspended, so nothing actually
  // played. Now common causes of silence (Chrome auto-suspend after tab idle,
  // first notification arriving before any user click) are handled.
  // "interrupted" is what iOS/Safari reports after a call or a screen lock; it needs the
  // same resume() as "suspended".
  if (ctx.state !== "running") {
    try { await ctx.resume(); } catch { /* refused: kept below */ }
  }
  if (ctx.state !== "running") { pendingSound = { category: category ?? null }; return; }
  pendingSound = null;
  try {
    const now = ctx.currentTime + 0.01;
    // Master chain: a limiter/compressor lets us drive the level HARD without
    // clipping, then a makeup gain pushes the perceived loudness way up vs. the
    // old quiet sine beeps.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -24; comp.knee.value = 30; comp.ratio.value = 12;
    comp.attack.value = 0.003; comp.release.value = 0.12;
    const master = ctx.createGain();
    master.gain.value = 2.2;                 // makeup — much louder
    comp.connect(master).connect(ctx.destination);

    const motif = MOTIFS[soundKeyFor(category)];
    for (const n of motif) {
      const start = now + n.t;
      const end = start + n.d;
      // Layer the fundamental + an octave above for a fuller, louder tone.
      for (const [mult, peak] of [[1, 0.9], [2, 0.35]] as const) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = n.type ?? "triangle";
        osc.frequency.value = n.f * mult;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0008, end);
        osc.connect(gain).connect(comp);
        osc.start(start);
        osc.stop(end + 0.02);
      }
    }
  } catch { /* swallow */ }
}

interface Notif {
  id: string;
  title: string;
  preview: string;
  category: string | null;
  link: string | null;
  source_id: string | null;
  read_at: string | null;
  created_at: string;
}

interface CategoryStyle { Icon: LucideIcon; title: string; bg: string; iconColor: string; }
function categoryStyle(category: string | null | undefined): CategoryStyle {
  const c = category ?? "";
  if (c === "chat_mention") return { Icon: AtSign, title: "You were mentioned", bg: "bg-orange-100", iconColor: "text-orange-600" };
  if (c === "dm" || c.startsWith("chat_")) return { Icon: MessageCircle, title: "New message", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c.startsWith("pto_") || c === "callout_sick") return { Icon: Plane, title: "Time off update", bg: "bg-blue-100", iconColor: "text-blue-600" };
  if (c === "clock_warn" || c === "clock_auto_close" || c.startsWith("overage_")) return { Icon: Clock, title: "Time clock", bg: "bg-orange-100", iconColor: "text-orange-600" };
  if (c.startsWith("ticket_")) return { Icon: Ticket, title: "Support ticket", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "announcement") return { Icon: Megaphone, title: "Announcement", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "review_sent" || c === "review_due") return { Icon: ClipboardList, title: "Performance review", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c.startsWith("expire_")) return { Icon: FileWarning, title: "Expiring soon", bg: "bg-orange-100", iconColor: "text-orange-600" };
  if (c === "kudos") return { Icon: PartyPopper, title: "You got kudos!", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "shift_change" || c === "swap_request") return { Icon: Clock, title: "Shift update", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "discipline") return { Icon: FileWarning, title: "HR notice", bg: "bg-red-100", iconColor: "text-red-700" };
  if (c.startsWith("order_")) return { Icon: ShoppingCart, title: "Order update", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "bug" || c === "production_task") return { Icon: Bug, title: "Update", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "crm_assigned") return { Icon: Handshake, title: "Assigned to you", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
  if (c === "pin_reset_request") return { Icon: Bell, title: "PIN reset requested", bg: "bg-orange-100", iconColor: "text-orange-600" };
  return { Icon: Bell, title: "Notification", bg: "bg-teal-mint", iconColor: "text-teal-dark" };
}

/**
 * One notification row.
 *
 * ── WHY THE CHEVRON EXISTS ──────────────────────────────────────────────────
 * The whole card is a link, so the only way to read a long alert used to be
 * following it to whatever it pointed at — the message itself was unreadable
 * past two lines. The chevron appears only when the text is actually clipped
 * (measured, not guessed from length), and expanding reads as reading: the
 * item is marked read, and the click never navigates or closes the panel.
 */
function NotifRow({ n, onOpen, onExpand }: {
  n: Notif;
  onOpen: (n: Notif, e: React.MouseEvent) => void;
  onExpand: (id: string) => void;
}) {
  const style = categoryStyle(n.category);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const prevRef = useRef<HTMLDivElement | null>(null);

  // Is the clamp actually hiding anything? Measured on mount and when the
  // text changes; +1 tolerates sub-pixel rounding.
  useEffect(() => {
    const el = prevRef.current;
    if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [n.preview]);

  return (
    <Link
      to={n.link || "/"}
      onClick={(e) => onOpen(n, e)}
      className={`block border-b border-line/70 px-4 py-3 transition hover:bg-offwhite ${n.read_at ? "" : "bg-teal-mint/20"}`}
    >
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${style.bg} ${style.iconColor}`}>
          <style.Icon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-semibold text-charcoal">{n.title || style.title}</div>
            {!n.read_at && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-dark" />}
          </div>
          <div ref={prevRef} className={`${expanded ? "whitespace-pre-wrap" : "line-clamp-2"} text-xs text-gray2`}>
            {n.preview}
          </div>
          <div className="mt-0.5 text-[10px] text-gray2">{timeAgo(n.created_at)}</div>
        </div>
        {(clipped || expanded) && (
          <button
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              setExpanded(x => !x);
              if (!expanded && !n.read_at) onExpand(n.id);
            }}
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray2 hover:bg-teal-mint hover:text-teal-dark"
            title={expanded ? "Show less" : "Show the full message"}
            aria-label={expanded ? "Show less" : "Show the full message"}
            aria-expanded={expanded}
          >
            <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
    </Link>
  );
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationBell() {
  const { employee } = useAuth();
  const [items, setItems] = useState<Notif[]>([]);
  const { openConversation } = useChatDock();
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [soundOn, setSoundOn] = useState<boolean>(readSoundPref);
  const [pushActive, setPushActive] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  useEffect(() => { void isPushActive().then(setPushActive); }, [employee?.id]);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Prime + keep AudioContext warm. Chrome auto-suspends after the tab has
  // been backgrounded; resuming inside a user-gesture handler is the only way
  // to wake it back up cleanly. Listeners stay attached for the lifetime of
  // the component, not just `once`, so any later click after a suspend re-wakes
  // the context.
  useEffect(() => {
    const prime = () => {
      const ctx = getAudio();
      if (!ctx) return;
      if (ctx.state !== "running") {
        void ctx.resume().then(() => {
          // A message came in while the sound was blocked: play it now that it can.
          if (pendingSound && ctx.state === "running" && soundOnRef.current) void playNotifSound(pendingSound.category);
        }).catch(() => {});
      }
    };
    // pointerdown/touchend cover phones, where "click" can be late or missing.
    const events = ["click", "keydown", "pointerdown", "touchend"] as const;
    for (const e of events) window.addEventListener(e, prime);
    return () => { for (const e of events) window.removeEventListener(e, prime); };
  }, []);

  function toggleSound() {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem(SOUND_PREF_KEY, next ? "1" : "0");
    if (next) { void playNotifSound(null); } // sample chirp on enable
  }

  const load = useCallback(async () => {
    if (!employee) return;
    // Cut initial load: top 10 covers the bell badge + recent unread.
    // User can scroll for more if needed (currently the panel only shows what
    // we load — if they need a longer history we can add "load more" later).
    const { data } = await supabase.from("hq_notifications").select("*")
      .eq("recipient_id", employee.id)
      .order("created_at", { ascending: false })
      .limit(10);
    setItems((data as Notif[]) ?? []);
  }, [employee?.id]);

  useEffect(() => { void load(); }, [load]);

  // The live feed can go quiet without telling anyone (a phone locks, a laptop sleeps, a
  // tab sits in the background), and then a new message creates its notification but
  // nothing on this page hears about it. So look again when the tab comes back, and every
  // 45 seconds while it is open, and chime for anything unread that was not here before.
  const knownIds = useRef<Set<string>>(new Set());
  useEffect(() => { for (const n of items) knownIds.current.add(n.id); }, [items]);
  useEffect(() => {
    if (!employee) return;
    const catchUp = async () => {
      const { data } = await supabase.from("hq_notifications").select("*")
        .eq("recipient_id", employee.id).is("read_at", null)
        .order("created_at", { ascending: false }).limit(10);
      const fresh = ((data as Notif[]) ?? []).filter(n => !knownIds.current.has(n.id));
      if (fresh.length === 0) return;
      for (const n of fresh) knownIds.current.add(n.id);
      setItems(prev => [...fresh, ...prev.filter(p => !fresh.some(f => f.id === p.id))]);
      if (soundOnRef.current) void playNotifSound(fresh[0].category);
      window.dispatchEvent(new CustomEvent("hq-toast", { detail: fresh[0] }));
    };
    const onVisible = () => { if (document.visibilityState === "visible") void catchUp(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    const timer = window.setInterval(() => void catchUp(), 45000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
      window.clearInterval(timer);
    };
  }, [employee?.id]);

  // Realtime subscription: pop a desktop notification + toast on new items
  useEffect(() => {
    if (!employee) return;
    const ch = supabase.channel(`notifs-${employee.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "hq_notifications",
        filter: `recipient_id=eq.${employee.id}`,
      }, payload => {
        const n = payload.new as Notif;
        if (knownIds.current.has(n.id)) return; // already caught by the 45-second check
        knownIds.current.add(n.id);
        setItems(prev => [n, ...prev]);
        // Desktop notification — only if permission was granted
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const native = new Notification(n.title, {
              body: n.preview,
              icon: "/assets/favicon-64.png",
              badge: "/assets/favicon-64.png",
              tag: n.id,
            });
            native.onclick = () => {
              window.focus();
              if (n.link) window.location.href = n.link;
              native.close();
            };
          } catch {}
        }
        // Play notification sound if enabled. Async resume happens inside —
        // intentionally not awaited so the realtime callback returns promptly.
        if (soundOnRef.current) { void playNotifSound(n.category); }
        // Always trigger a brief in-app toast as well via custom event
        window.dispatchEvent(new CustomEvent("hq-toast", { detail: n }));
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [employee?.id]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = items.filter(i => !i.read_at).length;

  async function markAllRead() {
    if (!employee || unread === 0) return;
    const now = new Date().toISOString();
    setItems(prev => prev.map(i => i.read_at ? i : { ...i, read_at: now }));
    await supabase.from("hq_notifications").update({ read_at: now })
      .eq("recipient_id", employee.id).is("read_at", null);
  }

  async function markRead(id: string) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, read_at: new Date().toISOString() } : i));
    await supabase.from("hq_notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  }

  async function requestPerm() {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setPerm(p);
  }

  // Subscribe THIS device to real push (works on the phone even when the app is
  // closed). On iOS this only works from the installed home-screen app.
  async function enableOnThisDevice() {
    if (!employee) return;
    setPushBusy(true); setPushMsg(null);
    const r = await enablePush(employee.id);
    setPushBusy(false);
    if (r.ok) { setPushActive(true); setPerm("granted"); setPushMsg(null); }
    else if (r.reason === "denied") setPushMsg("Notifications are blocked. Enable them for this app in your phone's Settings, then try again.");
    else if (r.reason === "unsupported") setPushMsg("This device/browser can't do push. On iPhone, add the app to your Home Screen first.");
    else setPushMsg(r.message || "Couldn't enable notifications. Try again.");
  }

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-offwhite"
        title="Notifications"
      >
        <span className="text-xl">🔔</span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-bad px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-xl bg-white shadow-soft ring-1 ring-line">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <h3 className="text-sm font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSound}
                className={`flex h-7 w-7 items-center justify-center rounded-full transition ${soundOn ? "text-teal-dark hover:bg-teal-mint" : "text-gray2 hover:bg-offwhite"}`}
                title={soundOn ? "Sound on — click to mute" : "Sound off — click to enable"}
                aria-label="Toggle notification sound"
              >
                {soundOn ? <Bell size={14} /> : <BellOff size={14} />}
              </button>
              <button onClick={markAllRead} className="text-xs text-teal-dark hover:underline" disabled={unread === 0}>
                Mark all read
              </button>
            </div>
          </div>

          {pushSupported() ? (
            !pushActive && (
              <button
                onClick={enableOnThisDevice}
                disabled={pushBusy}
                className="block w-full bg-teal-mint/50 px-4 py-2 text-left text-xs font-medium text-teal-dark hover:bg-teal-mint disabled:opacity-60"
              >
                {pushBusy ? "Enabling…" : "🔔 Turn on notifications for this device →"}
              </button>
            )
          ) : perm === "default" ? (
            <button
              onClick={requestPerm}
              className="block w-full bg-teal-mint/50 px-4 py-2 text-left text-xs text-teal-dark hover:bg-teal-mint"
            >
              💡 Enable desktop notifications so you don't miss anything →
            </button>
          ) : null}
          {pushActive && (
            <div className="bg-ok-bg px-4 py-1.5 text-left text-[11px] text-ok">✓ Push notifications on for this device</div>
          )}
          {pushMsg && (
            <div className="bg-warn-bg px-4 py-2 text-left text-[11px] text-warn">{pushMsg}</div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray2">You're caught up.</p>
            ) : items.map(n => (
              <NotifRow
                key={n.id}
                n={n}
                onExpand={(id) => { void markRead(id); }}
                onOpen={(notif, e) => {
                  setOpen(false); void markRead(notif.id);
                  const conv = chatConvFromLink(notif.link);
                  if (conv) { e.preventDefault(); openConversation(conv); }
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// In-app toast — listens to hq-toast events from the bell. Use anywhere in the app.
export function NotificationToast() {
  const [toasts, setToasts] = useState<Notif[]>([]);
  useEffect(() => {
    const onToast = (e: Event) => {
      const n = (e as CustomEvent<Notif>).detail;
      setToasts(prev => [...prev, n]);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== n.id)), 6000);
    };
    window.addEventListener("hq-toast", onToast);
    return () => window.removeEventListener("hq-toast", onToast);
  }, []);

  return (
    <div className="pointer-events-none fixed top-16 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map(n => {
        const s = categoryStyle(n.category);
        const isMention = n.category === "chat_mention";
        return (
          <Link
            key={n.id}
            to={n.link || "/"}
            className={`pointer-events-auto rounded-xl bg-white p-3 shadow-soft ring-1 transition hover:ring-teal animate-in ${isMention ? "ring-orange-400" : "ring-line"}`}
          >
            <div className="flex items-start gap-2.5">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${s.bg} ${s.iconColor}`}>
                <s.Icon size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-charcoal">{n.title || s.title}</div>
                <div className="line-clamp-2 text-xs text-gray2">{n.preview}</div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
